/**
 * Core validation logic for aspect ratios
 */

import { AspectRatioResult } from "./types";
import { getFileDimensions, formatAspectRatio } from "./utils";

/**
 * Validates if a file has the correct aspect ratio
 */
export async function validateAspectRatio(
  file: File,
  expectedRatio: number,
  tolerance: number = 0.08
): Promise<AspectRatioResult> {
  try {
    const dimensions = await getFileDimensions(file);
    const actualRatio = dimensions.width / dimensions.height;
    const ratioDifference = Math.abs(actualRatio - expectedRatio);
    const isValid = ratioDifference <= tolerance;

    return {
      isValid,
      actualRatio,
      errorMessage: isValid
        ? undefined
        : `Invalid aspect ratio. Expected ${formatAspectRatio(
            expectedRatio
          )}, got ${formatAspectRatio(actualRatio)}. Tolerance: ±${(
            tolerance * 100
          ).toFixed(1)}%`,
    };
  } catch (error) {
    return {
      isValid: false,
      actualRatio: 0,
      errorMessage: `Failed to validate aspect ratio: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
    };
  }
}

/**
 * Validates if a file has a square aspect ratio (1:1)
 * @param file - The File object to validate
 * @param tolerance - Optional tolerance (default 0.08 = 8%)
 * @returns Promise<AspectRatioResult>
 */
export async function validateSquareAspectRatio(
  file: File,
  tolerance: number = 0.08
): Promise<AspectRatioResult> {
  return validateAspectRatio(file, 1.0, tolerance);
}

/**
 * Validates if a file has a portrait aspect ratio (9:16)
 * @param file - The File object to validate
 * @param tolerance - Optional tolerance (default 0.08 = 8%)
 * @returns Promise<AspectRatioResult>
 */
export async function validatePortraitAspectRatio(
  file: File,
  tolerance: number = 0.08
): Promise<AspectRatioResult> {
  return validateAspectRatio(file, 0.56, tolerance);
}

/**
 * Validates if a file has a landscape aspect ratio (1.91:1)
 * @param file - The File object to validate
 * @param tolerance - Optional tolerance (default 0.08 = 8%)
 * @returns Promise<AspectRatioResult>
 */
export async function validateLandscapeAspectRatio(
  file: File,
  tolerance: number = 0.08
): Promise<AspectRatioResult> {
  return validateAspectRatio(file, 1.91, tolerance);
}
